import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, PayLineHistory as History, PayLineHistoryRow } from '../../utils/api';
import { money, fmtDateTime } from '../../utils/region';
import { Dialog, Loader, emptyLine } from '../CompanyWorkspace/dialog';

// ── A PND.1 / SSO line's History (b13 S3, PAY-6) ───────────────────────────────────────────────────────────────
// Read-only: one GET (server/src/services/payLines.ts lineHistory) and nothing else. Every wage, ID number and name
// change a person made to this line — added, edited (and the fixed amount an edit cleared), deleted, Generate /
// Re-sync from the roster, a salary change carried into the month — oldest first, with who and through which door;
// and, on top, where the line came from, read off the line itself (the roster or a hand, rebuilt by the system,
// written by a payroll run, or replaced by one). The panels open it only when the month payload says `history: true`
// (canSeePay — O2's white list, as the employee window's History), so this card is never refused.
// ⛔ The door is the dictionary's (plog_src_*), mapped from `source` by prefix — never the raw source string.

export type PayLineKind = 'pnd1' | 'sso';

/** The door a row came through, in the reader's words: the source's prefix, never the source itself. */
export function payDoorKey(source: string): string {
  const s = String(source || '');
  if (s.startsWith('route:pnd1.')) return 'plog_src_pnd1';
  if (s.startsWith('route:sso.')) return 'plog_src_sso';
  if (s.startsWith('employee:portal:')) return 'plog_src_portal';
  if (s.startsWith('employee:')) return 'plog_src_employee';
  return 'plog_src_other';
}

const dash = (v: any) => (v === null || v === undefined || String(v) === '' ? '—' : String(v));
/** A value as recorded: a wage or a fixed amount as a figure, an ID or a name as typed; a cleared amount '—'. */
const valueOf = (field: string, v: any): string => (field === 'wage' || field === 'override' ? (v === null || v === undefined ? '—' : money(v)) : dash(v));
/** A whole line (add, remove, resync_*): its ID, name and wage. */
function lineOf(json: any): string {
  let o: any = null;
  try { o = typeof json === 'string' ? JSON.parse(json) : null; } catch { /* not JSON: shown as it is */ }
  if (!o) return dash(json);
  return [dash(o.id_number), dash(o.name), money(o.wage)].join(' · ');
}

export default function PayLineHistory({ kind, lineId, onClose }: { kind: PayLineKind; lineId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [data, setData] = useState<History | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    (kind === 'pnd1' ? api.pnd1LineHistory(lineId) : api.ssoLineHistory(lineId))
      .then(d => { if (alive) setData(d); })
      .catch((e: any) => { if (alive) setErr(t('plog_load_failed', { error: String(e?.message || e) })); });
    return () => { alive = false; };
  }, [kind, lineId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const o = data?.origin;
  const when = o ? fmtDateTime(o.createdAt) : '';
  const origin: string[] = !o ? [] : [
    o.source === 'payroll' ? t('plog_origin_payroll', { docNos: o.payrollRef }) : o.fromRoster ? t('plog_origin_roster', { date: when }) : t('plog_origin_hand', { date: when }),
    ...(o.backfilled ? [t('plog_origin_backfilled')] : []),
    ...(o.takenBy ? [t('plog_origin_taken', { docNo: o.takenBy.docNo })] : []),
  ];
  const change = (r: PayLineHistoryRow): string => (r.field
    ? `${t(`plog_field_${r.field}`)}: ${valueOf(r.field, r.old)} → ${valueOf(r.field, r.new)}`
    : lineOf(r.action === 'remove' || r.action === 'resync_remove' ? r.old : r.new));
  const line = data?.line;

  return (
    <Dialog title={t('plog_title')} docNo={line ? `${dash(line.name)} · ${dash(line.id_number)}` : undefined} onClose={onClose} width={640}>
      <div data-plog-card={kind} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {err ? <div style={{ fontSize: 12.5, color: '#B91C1C' }} data-plog-error>{err}</div>
          : !data ? <Loader />
          : (
            <>
              <div data-plog-origin style={{ fontSize: 12, color: 'var(--text-secondary)', background: 'var(--bg-app)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px', lineHeight: 1.5 }}>
                <i className="ti ti-info-circle" style={{ marginRight: 6 }} aria-hidden />{origin.join(' ')}
              </div>
              {!data.rows.length ? <div style={emptyLine} data-plog-empty>{t('plog_empty')}</div> : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '60vh', overflowY: 'auto' }}>
                  {data.rows.map(r => {
                    const door = t(payDoorKey(r.source));
                    return (
                      <div key={r.id} data-plog-row={r.id} data-plog-action={r.action} data-plog-field={r.field}
                        style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '7px 10px', fontSize: 12.5, lineHeight: 1.5 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                          <b>{t(`plog_act_${r.action}`)}</b>
                          <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{fmtDateTime(r.at)}</span>
                        </div>
                        <div data-plog-text>
                          <span data-plog-change>{change(r)}</span>
                          {' · '}<span data-plog-by>{t('plog_by', { name: r.actorName })}</span>
                          {' · '}<span data-plog-door title={t('plog_through', { door })}>{door}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
      </div>
    </Dialog>
  );
}
